import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'motion/react';
import { cn } from '@/src/infrastructure/lib/utils';
import { SceneMedia } from '../SceneMedia';
import type { SceneAssets, MobileBehavior } from '../manifest';

export interface StoryPanel {
  id: string;
  assets: SceneAssets;
  mobileBehavior: MobileBehavior;
  content: React.ReactNode;
}

export interface StickyScrollStoryProps {
  panels: StoryPanel[];
  className?: string;
}

export function StickyScrollStory({ panels, className }: StickyScrollStoryProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();

  // Scroll tracking across the entire story
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  return (
    <div ref={containerRef} className={cn("relative w-full", className)}>
      <div className="flex flex-col md:flex-row w-full h-[300vh] relative">
        {/* Left/Top side: Sticky Images */}
        <div className="w-full md:w-1/2 h-[50vh] md:h-screen sticky top-0 overflow-hidden">
          {panels.map((panel, idx) => {
            // Each panel takes up an equal fraction of the scroll progress
            const step = 1 / panels.length;
            const start = idx * step;
            const end = start + step;
            
            // Fade in and out based on scroll progress
            const opacity = useTransform(
              scrollYProgress,
              [Math.max(0, start - 0.1), start, end, Math.min(1, end + 0.1)],
              [0, 1, 1, 0]
            );

            // Slight scale effect if motion is enabled
            const scale = useTransform(
              scrollYProgress,
              [start, end],
              [1, 1.1]
            );

            return (
              <motion.div
                key={`img-${panel.id}`}
                className="absolute inset-0 w-full h-full"
                style={{ 
                  opacity: shouldReduceMotion ? (idx === 0 ? 1 : 0) : opacity,
                  scale: shouldReduceMotion ? 1 : scale
                }}
              >
                <SceneMedia assets={panel.assets} mobileBehavior={panel.mobileBehavior} />
              </motion.div>
            );
          })}
        </div>

        {/* Right/Bottom side: Scrolling Content */}
        <div className="w-full md:w-1/2 flex flex-col justify-between pt-[50vh] md:pt-0">
          {panels.map((panel, idx) => (
            <div 
              key={`text-${panel.id}`}
              className="h-[50vh] md:h-screen flex flex-col items-center justify-center p-8 text-center"
            >
              <div className="bg-surface/80 backdrop-blur-md p-8 rounded-3xl shadow-xl max-w-md w-full border border-outline-variant/20">
                {panel.content}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
